import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';
import { toast } from 'sonner';

import { api } from '@/lib/api';
import { apiErrorMessage } from '@/lib/clients';
import { useHasPermission } from '@/lib/permissions';
import { BILLING_MODE_LABELS, BILLING_RATE_UNIT, dayFr, gnf, invoiceBadge } from '@/lib/invoices';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

import type { Client, ClientBillingMode, CustomerInvoice } from '@logistech/ui-shared';

/** Tarif contractuel du client (base de ses factures) + ses factures. */
export function ClientBillingTab({ client }: { client: Client }) {
  const qc = useQueryClient();
  const canWrite = useHasPermission('missions.write');
  const [editing, setEditing] = useState(canWrite && !client.billingMode);
  const [form, setForm] = useState({ mode: '', rate: '', vat: '', terms: '' });

  useEffect(() => {
    setForm({
      mode: client.billingMode ?? '',
      rate: client.billingRate?.toString() ?? '',
      vat: client.vatRate.toString(),
      terms: client.paymentTermsDays.toString(),
    });
  }, [client, editing]);

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/clients/${client.id}`, {
        billingMode: form.mode as ClientBillingMode,
        billingRate: Number(form.rate),
        vatRate: Number(form.vat),
        paymentTermsDays: Number(form.terms),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['client', client.id] });
      toast.success('Tarif enregistré');
      setEditing(false);
    },
    onError: (err: unknown) => toast.error(apiErrorMessage(err, 'Erreur lors de l’enregistrement du tarif')),
  });

  const { data: invoices } = useQuery({
    queryKey: ['customer-invoices', { clientId: client.id }],
    queryFn: async () =>
      (await api.get<{ data: CustomerInvoice[] }>(`/customer-invoices?clientId=${client.id}&limit=100`)).data,
  });

  const valid = form.mode !== '' && form.rate !== '' && Number(form.rate) >= 0
    && form.vat !== '' && Number(form.vat) >= 0 && Number(form.vat) <= 100
    && /^\d+$/.test(form.terms);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Tarif transport</CardTitle>
          {canWrite && !editing && (
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              <Pencil className="h-4 w-4 mr-1" /> Modifier
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {canWrite && editing ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Mode de facturation</Label>
                  <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
                    <SelectTrigger><SelectValue placeholder="Choisir" /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(BILLING_MODE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="rate">
                    Prix HT (GNF{form.mode ? ` / ${BILLING_RATE_UNIT[form.mode as ClientBillingMode]}` : ''})
                  </Label>
                  <Input id="rate" inputMode="numeric" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} placeholder="150000" />
                </div>
                <div>
                  <Label htmlFor="vat">TVA (%)</Label>
                  <Input id="vat" inputMode="decimal" value={form.vat} onChange={(e) => setForm({ ...form, vat: e.target.value })} />
                </div>
                <div>
                  <Label htmlFor="terms">Délai de paiement (jours)</Label>
                  <Input id="terms" inputMode="numeric" value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Tonnes = quantités livrées saisies par le chauffeur (à défaut, poids prévu de la mission) ;
                km = distance estimée de la mission. Les quantités restent modifiables sur le brouillon de facture.
              </p>
              <div className="flex justify-end gap-2">
                {client.billingMode && <Button variant="outline" onClick={() => setEditing(false)}>Annuler</Button>}
                <Button onClick={() => save.mutate()} disabled={!valid || save.isPending}>
                  {save.isPending ? 'Enregistrement…' : 'Enregistrer le tarif'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4 text-sm">
              <Info label="Mode" value={client.billingMode ? BILLING_MODE_LABELS[client.billingMode] : '—'} />
              <Info
                label="Prix HT"
                value={client.billingMode && client.billingRate != null
                  ? `${gnf(client.billingRate)} / ${BILLING_RATE_UNIT[client.billingMode]}` : '—'}
              />
              <Info label="TVA" value={`${client.vatRate} %`} />
              <Info label="Délai de paiement" value={`${client.paymentTermsDays} jours`} />
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Factures</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>N°</TableHead>
                <TableHead>Période</TableHead>
                <TableHead className="text-right">Total TTC</TableHead>
                <TableHead>Statut</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices?.data.map((i) => (
                <TableRow key={i.id}>
                  <TableCell>
                    <Link to="/invoices/$id" params={{ id: i.id }} className="font-mono text-primary-500 hover:underline">
                      {i.number ?? 'Brouillon'}
                    </Link>
                  </TableCell>
                  <TableCell>{dayFr(i.periodStart)} – {dayFr(i.periodEnd)}</TableCell>
                  <TableCell className="text-right">{gnf(i.total, i.currency)}</TableCell>
                  <TableCell><Badge variant={invoiceBadge(i).variant}>{invoiceBadge(i).label}</Badge></TableCell>
                </TableRow>
              ))}
              {invoices && invoices.data.length === 0 && (
                <TableRow><TableCell colSpan={4} className="text-muted-foreground">Aucune facture</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

const Info = ({ label, value }: { label: string; value: string }) => (
  <div>
    <dt className="text-muted-foreground text-xs uppercase tracking-wide">{label}</dt>
    <dd className="font-medium mt-0.5">{value}</dd>
  </div>
);
